/** Plain-array indicators. Every function returns an array the same length as the input,
 *  with NaN where there is not enough history yet. */

const nan = (n) => new Array(n).fill(NaN);

export function sma(values, period) {
  const out = nan(values.length);
  let sum = 0;
  for (let i = 0; i < values.length; i += 1) {
    sum += values[i];
    if (i >= period) sum -= values[i - period];
    if (i >= period - 1) out[i] = sum / period;
  }
  return out;
}

export function ema(values, period) {
  const out = nan(values.length);
  const k = 2 / (period + 1);
  let prev = NaN;
  for (let i = 0; i < values.length; i += 1) {
    if (i === period - 1) {
      let s = 0;
      for (let j = 0; j < period; j += 1) s += values[j];
      prev = s / period;
      out[i] = prev;
    } else if (i >= period) {
      prev = values[i] * k + prev * (1 - k);
      out[i] = prev;
    }
  }
  return out;
}

export function stdev(values, period) {
  const out = nan(values.length);
  for (let i = period - 1; i < values.length; i += 1) {
    let m = 0;
    for (let j = i - period + 1; j <= i; j += 1) m += values[j];
    m /= period;
    let v = 0;
    for (let j = i - period + 1; j <= i; j += 1) v += (values[j] - m) ** 2;
    out[i] = Math.sqrt(v / period);
  }
  return out;
}

/** Wilder smoothing (RMA), as used by RSI, ATR and ADX. */
export function rma(values, period) {
  const out = nan(values.length);
  let prev = NaN;
  for (let i = 0; i < values.length; i += 1) {
    if (i === period - 1) {
      let s = 0;
      for (let j = 0; j < period; j += 1) s += values[j];
      prev = s / period;
      out[i] = prev;
    } else if (i >= period) {
      prev = (prev * (period - 1) + values[i]) / period;
      out[i] = prev;
    }
  }
  return out;
}

export function rsi(close, period = 14) {
  const gains = nan(close.length);
  const losses = nan(close.length);
  for (let i = 1; i < close.length; i += 1) {
    const d = close[i] - close[i - 1];
    gains[i] = d > 0 ? d : 0;
    losses[i] = d < 0 ? -d : 0;
  }
  // first value is NaN; shift so Wilder smoothing starts at index 1
  const g = rma(gains.slice(1), period);
  const l = rma(losses.slice(1), period);
  const out = nan(close.length);
  for (let i = 0; i < g.length; i += 1) {
    if (!Number.isFinite(g[i])) continue;
    out[i + 1] = l[i] === 0 ? 100 : 100 - 100 / (1 + g[i] / l[i]);
  }
  return out;
}

export function macd(close, fast = 12, slow = 26, signal = 9) {
  const f = ema(close, fast);
  const s = ema(close, slow);
  const line = close.map((_, i) => (Number.isFinite(f[i]) && Number.isFinite(s[i]) ? f[i] - s[i] : NaN));
  const firstIdx = line.findIndex((v) => Number.isFinite(v));
  const sig = nan(close.length);
  if (firstIdx >= 0) {
    const part = ema(line.slice(firstIdx), signal);
    part.forEach((v, i) => {
      sig[i + firstIdx] = v;
    });
  }
  const hist = line.map((v, i) => (Number.isFinite(v) && Number.isFinite(sig[i]) ? v - sig[i] : NaN));
  return { line, signal: sig, hist };
}

export function bollinger(close, period = 20, mult = 2) {
  const mid = sma(close, period);
  const sd = stdev(close, period);
  return {
    mid,
    upper: mid.map((m, i) => m + mult * sd[i]),
    lower: mid.map((m, i) => m - mult * sd[i]),
  };
}

export function trueRange(high, low, close) {
  return high.map((h, i) => (i === 0 ? h - low[i] : Math.max(h - low[i], Math.abs(h - close[i - 1]), Math.abs(low[i] - close[i - 1]))));
}

export function atr(high, low, close, period = 14) {
  return rma(trueRange(high, low, close), period);
}

export function stochastic(high, low, close, kPeriod = 14, dPeriod = 3) {
  const k = nan(close.length);
  for (let i = kPeriod - 1; i < close.length; i += 1) {
    let hh = -Infinity;
    let ll = Infinity;
    for (let j = i - kPeriod + 1; j <= i; j += 1) {
      if (high[j] > hh) hh = high[j];
      if (low[j] < ll) ll = low[j];
    }
    k[i] = hh === ll ? 50 : ((close[i] - ll) / (hh - ll)) * 100;
  }
  const firstIdx = k.findIndex((v) => Number.isFinite(v));
  const d = nan(close.length);
  if (firstIdx >= 0) sma(k.slice(firstIdx), dPeriod).forEach((v, i) => { d[i + firstIdx] = v; });
  return { k, d };
}

/** Highest high / lowest low of the PREVIOUS `period` bars (so a breakout can be detected). */
export function donchian(high, low, period = 20) {
  const upper = nan(high.length);
  const lower = nan(high.length);
  for (let i = period; i < high.length; i += 1) {
    let hh = -Infinity;
    let ll = Infinity;
    for (let j = i - period; j < i; j += 1) {
      if (high[j] > hh) hh = high[j];
      if (low[j] < ll) ll = low[j];
    }
    upper[i] = hh;
    lower[i] = ll;
  }
  return { upper, lower };
}

/** Supertrend. dir[i] = 1 (uptrend) or -1 (downtrend). */
export function supertrend(high, low, close, period = 10, mult = 3) {
  const a = atr(high, low, close, period);
  const n = close.length;
  const line = nan(n);
  const dir = nan(n);
  let finalUpper = NaN;
  let finalLower = NaN;
  let d = 1;
  for (let i = 0; i < n; i += 1) {
    if (!Number.isFinite(a[i])) continue;
    const hl2 = (high[i] + low[i]) / 2;
    const basicUpper = hl2 + mult * a[i];
    const basicLower = hl2 - mult * a[i];
    if (!Number.isFinite(finalUpper)) {
      finalUpper = basicUpper;
      finalLower = basicLower;
    } else {
      finalUpper = basicUpper < finalUpper || close[i - 1] > finalUpper ? basicUpper : finalUpper;
      finalLower = basicLower > finalLower || close[i - 1] < finalLower ? basicLower : finalLower;
      if (d === 1 && close[i] < finalLower) d = -1;
      else if (d === -1 && close[i] > finalUpper) d = 1;
    }
    dir[i] = d;
    line[i] = d === 1 ? finalLower : finalUpper;
  }
  return { line, dir };
}
